import { useCallback, useEffect, useRef, useState } from "react";

import debounce from "lodash.debounce";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { useToast } from "hooks/toast";

import { hideLoading, showLoading } from "store/modules/loading/actions";
import { useAppSelector } from "store/rootReducer";

import { AdminArea } from "components/Body/AdminArea";
import { Drawer } from "components/Drawer";

import { customerTemplateService } from "services/costumerTemplate";

import { LogOperation } from "utils/entities/logOperation";
import { FORMS_ADMIN } from "utils/entities/permission";
import { stringFormat } from "utils/stringFormat";

import { CreateEditCustomerTemplate } from "./CreateEdit";

//Toast

//Permissões

import { ICustomerTemplate, ISituation } from "./interface";
import { Container } from "./styles";

export function CustomerTemplate() {
  const { t } = useTranslation();
  const [listCustomerTemplate, setListCustomerTemplate] = useState<any>([]);
  const [openModal, setOpenModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [template, setTemplate] = useState<string>("");
  const [id, setId] = useState<number | undefined>();
  const [keywords, setKeywords] = useState<Array<string>>([]);
  const [actived, setActived] = useState(true);
  const [type, setType] = useState<number | undefined>();
  const [situation, setSituation] = useState<ISituation | null>(null);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const { user } = useAppSelector((state) => state.user);
  const { globalConfigurations } = useSelector(
    (state: any) => state.configurations,
  );

  const [fieldError, setFieldError] = useState<{
    type: boolean;
    title: boolean;
    description: boolean;
    template: boolean;
  }>({
    type: false,
    title: false,
    description: false,
    template: false,
  });

  const dispatch = useDispatch();
  const { addToast } = useToast();

  const customerId = user?.customerId;

  const fetchIdRef = useRef(0);

  useEffect(() => {
    const paginationList = Array.isArray(globalConfigurations)
      ? globalConfigurations.find((item: any) => item?.name === "PaginationList")
      : null;
    const paginationValue = paginationList?.value
      ? parseInt(paginationList.value)
      : 10;

    setPageSize(paginationValue);
  }, [globalConfigurations]);

  useEffect(() => {
    if (pageSize > 0) {
      setCurrentPage(0);
      onLoadList(0);
    }
  }, [customerId, situation, pageSize]);

  async function onLoadList(page?: number, searchTerm?: string) {
    const fetchId = ++fetchIdRef.current;
    dispatch(showLoading());
    setLoading(true);
    const { success, body, message } =
      await customerTemplateService.getAllByCustomerId({
        customerId,
        active: situation,
        type: null,
        pageNumber: (page ?? currentPage) + 1,
        pageSize,
        searchTerm: searchTerm ?? search,
      });

    if (fetchId !== fetchIdRef.current) return;

    if (success) {
      const paginatedData = body?.items ? body : body?.data ?? body;
      const items = Array.isArray(paginatedData)
        ? paginatedData
        : paginatedData?.items ?? [];
      const templateList = items.map((element: ICustomerTemplate) => ({
        ...element,
        name: element?.title,
      }));

      setListCustomerTemplate(templateList);
      setTotalCount(paginatedData?.totalCount ?? items.length);
    } else {
      addToast({
        type: "warning",
        title: message,
      });
    }
    if (fetchId === fetchIdRef.current) {
      dispatch(hideLoading());
      setLoading(false);
    }
    return [];
  }

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setCurrentPage(0);
      onLoadList(0, value);
    }, 400),
    [customerId, situation, pageSize],
  );

  useEffect(() => {
    return () => debouncedSearch.cancel();
  }, [debouncedSearch]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    debouncedSearch(value);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    onLoadList(page);
  };

  const serverPageCount = Math.ceil(totalCount / pageSize);

  function clear() {
    setIsEdit(false);
    setTitle("");
    setType(undefined);
    setDescription("");
    setTemplate("");
    setKeywords([]);
    setOpenModal(false);
    setId(undefined);
    setFieldError({
      type: false,
      template: false,
      description: false,
      title: false,
    });
    setActived(true);
  }

  function openModalClearAdd() {
    clear();
    setOpenModal(true);
  }

  function onClickProcedureCustomerSource({
    active,
    id,
    keywords,
    template,
    title,
    type,
    description,
  }: ICustomerTemplate) {
    setId(id);
    setIsEdit(true);
    setTitle(title);
    setDescription(description ?? "");
    setActived(active);
    setTemplate(template);
    setKeywords(keywords ? keywords?.split(";") : []);
    setType(type);
    setOpenModal(true);
  }

  function createBody(): any {
    return {
      id: id ? id : undefined,
      customerId: customerId,
      type: type,
      title: title,
      description: description,
      template: template,
      keywords: keywords ? keywords.join(";") : null,
      active: actived,
      createIn: new Date(),
      device: LogOperation.getDevice(),
      ip: LogOperation.getMachineIP(),
      logPoliceUnitId: LogOperation.getPoliceUnitId(),
      logUserId: LogOperation.getUserId(),
    };
  }

  async function postTemplate() {
    dispatch(showLoading());
    if (!title || !type || !template || !description) {
      addToast({
        type: "warning",
        title: t("templates.title"),
        description: t("templates.fillRequiredFields"),
      });
      dispatch(hideLoading());

      return setFieldError({
        template: !template,
        title: !title,
        description: !description,
        type: !type,
      });
    }

    if (type === 2 && !keywords.includes("Transcription")) {
      addToast({
        type: "warning",
        title: t("common.warning"),
        description: t("templates.transcriptionKeywordRequired"),
      });
      return dispatch(hideLoading());
    }

    const existName = stringFormat.verifyExistNameInArray(
      listCustomerTemplate,
      title,
    );

    if (existName) {
      addToast({
        type: "warning",
        title: t("common.warning"),
        description: t("templates.nameAlreadyExists"),
      });
      return dispatch(hideLoading());
    }

    dispatch(showLoading());

    setOpenModal(false);
    const { success, message } = await customerTemplateService.createTemplate(
      createBody() as any,
    );
    if (success) {
      addToast({
        type: "success",
        title: t("common.success"),
        description: t("templates.created"),
      });
      clear();
      await onLoadList();
    } else {
      addToast({
        type: "error",
        title: t("common.error"),
        description: message,
      });
    }

    setLoading(false);
    dispatch(hideLoading());
  }

  async function putTemplate() {
    setLoading(true);
    dispatch(showLoading());
    if (!title || !type || !template || !description) {
      addToast({
        type: "warning",
        title: t("templates.title"),
        description: t("templates.fillRequiredFieldsUpdate"),
      });
      setLoading(false);
      dispatch(hideLoading());

      return setFieldError({
        template: !template,
        title: !title,
        description: !description,
        type: !type,
      });
    }

    if (type === 2 && !keywords.includes("Transcription")) {
      addToast({
        type: "warning",
        title: t("common.warning"),
        description: t("templates.transcriptionKeywordRequired"),
      });
      setLoading(false);
      return dispatch(hideLoading());
    }

    const existName = stringFormat.verifyExistNameInArray(
      listCustomerTemplate,
      title,
      id,
    );

    if (existName) {
      addToast({
        type: "warning",
        title: t("common.warning"),
        description: t("templates.nameAlreadyExists"),
      });
      setLoading(false);
      return dispatch(hideLoading());
    }
    setLoading(true);
    dispatch(showLoading());

    const { success, message } =
      await customerTemplateService.updateTemplate(createBody());
    if (success) {
      addToast({
        type: "success",
        title: t("common.success"),
        description: t("templates.updated"),
      });

      clear();
      await onLoadList();
    } else {
      setActived(true);
      addToast({
        type: "error",
        title: t("common.error"),
        description: message,
      });
    }

    setLoading(false);
    dispatch(hideLoading());
  }

  const handleSitualtion = async (value: any) => {
    await localStorage.setItem("filter", value);
    setCurrentPage(0);
    switch (value) {
      case 0:
        return setSituation(false);
      case 1:
        return setSituation(true);
      case 2:
        return setSituation(null);
      default:
        return null;
    }
  };

  return (
    <Container>
      <AdminArea
        loading={loading}
        title={t("templates.title")}
        InputSearchLabel={t("common.searchBy")}
        InputSearchOnChange={handleSearchChange}
        list={listCustomerTemplate}
        headerAddButtonOnClick={() => openModalClearAdd()}
        headerTrashButtonOnClick={() => {}}
        onClick={(item: any) => onClickProcedureCustomerSource(item)}
        setOpenModal={setOpenModal}
        setIsEdit={setIsEdit}
        onChangeSituation={(value: any) => handleSitualtion(value)}
        permissionName={FORMS_ADMIN.CUSTOMER_TEMPLATE}
        searchText={search}
        serverSidePagination
        totalCount={totalCount}
        currentPage={currentPage}
        serverPageCount={serverPageCount}
        onPageChange={handlePageChange}
      />
      <Drawer
        title={
          isEdit ? t("templates.editTemplate") : t("templates.createTemplate")
        }
        open={openModal}
        onClose={() => clear()}
        onApply={isEdit ? putTemplate : postTemplate}
        paddingLeft={0}
        paddingRight={0}
      >
        <CreateEditCustomerTemplate
          isEdit={isEdit}
          setTitle={setTitle}
          title={title}
          setDescription={setDescription}
          description={description}
          actived={actived}
          fieldError={fieldError}
          setFieldError={setFieldError}
          setTemplate={setTemplate}
          setKeywords={setKeywords}
          keywords={keywords}
          setActived={() => setActived(!actived)}
          setType={setType}
          type={type}
          template={template}
        />
      </Drawer>
    </Container>
  );
}
